import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
  Button,
  Modal,
} from "react-bootstrap";
import api, { BASE_URL } from "../../services/api";
import "./Dashboard.css";

const jenisPelatihanLabel = {
  PPH01: "Penyelia Halal UMK Self Declare",
  PPH02: "Penyelia Halal UMK Reguler",
  PPH03: "Penyelia Halal Reguler",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PesertaDaftarCalon = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter options
  const [jenisList, setJenisList] = useState([]);
  const [angkatanList, setAngkatanList] = useState([]);

  // Selected filters
  const [filterJenis, setFilterJenis] = useState("");
  const [filterAngkatan, setFilterAngkatan] = useState("");
  const [searchText, setSearchText] = useState("");

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  // Modal terima
  const [showTerimaModal, setShowTerimaModal] = useState(false);
  const [targetTerima, setTargetTerima] = useState(null);

  // Modal tolak
  const [showTolakModal, setShowTolakModal] = useState(false);
  const [targetTolak, setTargetTolak] = useState(null);
  const [alasanTolak, setAlasanTolak] = useState("");
  const [alasanError, setAlasanError] = useState("");

  // Modal hapus
  const [showHapusModal, setShowHapusModal] = useState(false);
  const [targetHapus, setTargetHapus] = useState(null);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterJenis, filterAngkatan]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/pendaftaran-penyelia/filter-options");
      if (res.data.success) {
        setJenisList(res.data.jenis_pelatihan || []);
        setAngkatanList(res.data.angkatan || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filterJenis) params.jenis_pelatihan = filterJenis;
      if (filterAngkatan) params.angkatan = filterAngkatan;
      const res = await api.get("/pendaftaran-penyelia/daftar-calon", { params });
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError("Gagal memuat data.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data dari server.");
    } finally {
      setLoading(false);
    }
  }, [filterJenis, filterAngkatan]);

  const filteredData = data.filter((row) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      row.nama?.toLowerCase().includes(term) ||
      row.email?.toLowerCase().includes(term) ||
      row.no_hp?.toLowerCase().includes(term) ||
      row.nik?.toLowerCase().includes(term) ||
      row.no_register?.toLowerCase().includes(term)
    );
  });

  // --- Terima ---
  const openTerimaModal = (row) => {
    setTargetTerima(row);
    setActionError("");
    setShowTerimaModal(true);
  };

  const handleTerima = async () => {
    if (!targetTerima) return;
    try {
      setActionLoading(true);
      setActionError("");
      await api.put("/pendaftaran-penyelia/terima", { email: targetTerima.email });
      setShowTerimaModal(false);
      setTargetTerima(null);
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || "Gagal mengubah status.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Hapus ---
  const openHapusModal = (row) => {
    setTargetHapus(row);
    setActionError("");
    setShowHapusModal(true);
  };

  const handleHapus = async () => {
    if (!targetHapus) return;
    try {
      setActionLoading(true);
      setActionError("");
      await api.delete(`/pendaftaran-penyelia/calon/${targetHapus.no_register}`);
      setShowHapusModal(false);
      setTargetHapus(null);
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || "Gagal menghapus data.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Tolak ---
  const openTolakModal = (row) => {
    setTargetTolak(row);
    setAlasanTolak("");
    setAlasanError("");
    setActionError("");
    setShowTolakModal(true);
  };

  const handleTolak = async () => {
    if (!targetTolak) return;
    if (!alasanTolak.trim()) {
      setAlasanError("Alasan penolakan wajib diisi.");
      return;
    }
    try {
      setActionLoading(true);
      setActionError("");
      await api.put("/pendaftaran-penyelia/tolak", {
        email: targetTolak.email,
        keterangan: alasanTolak.trim(),
      });
      setShowTolakModal(false);
      setTargetTolak(null);
      setAlasanTolak("");
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || "Gagal menolak pendaftaran.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container fluid>
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-user-clock me-2"></i>
          Daftar Calon Penyelia Halal
        </h2>
        <p className="text-muted mb-0">
          Peserta yang telah mendaftar dengan status{" "}
          <Badge bg="warning" text="dark">PENDAFTARAN</Badge>
        </p>
      </div>

      {/* Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => {
                  setFilterJenis(e.target.value);
                  setFilterAngkatan("");
                }}
              >
                <option value="">— Semua —</option>
                {jenisList.map((j) => (
                  <option key={j} value={j}>
                    {jenisPelatihanLabel[j] || j}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Angkatan</Form.Label>
              <Form.Select
                value={filterAngkatan}
                onChange={(e) => setFilterAngkatan(e.target.value)}
              >
                <option value="">— Semua —</option>
                {angkatanList.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold mb-1">Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, email, NIK, no. register..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={2} className="text-end">
              <span className="text-muted small">{filteredData.length} data</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>No. Register</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>No. HP</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Angkatan</th>
                  <th>Tgl. Daftar</th>
                  <th className="text-center">Berkas</th>
                  <th className="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => (
                    <tr key={row.email}>
                      <td className="ps-3">{idx + 1}</td>
                      <td>{row.no_register || "-"}</td>
                      <td className="fw-semibold">{row.nama || "-"}</td>
                      <td>{row.email}</td>
                      <td>{row.no_hp || "-"}</td>
                      <td>{row.nik || "-"}</td>
                      <td>{jenisPelatihanLabel[row.jenis_pelatihan] || row.jenis_pelatihan || "-"}</td>
                      <td>{row.angkatan || "-"}</td>
                      <td>{formatDate(row.tgl_daftar)}</td>
                      <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                        {row.file_ktp ? (
                          <a
                            href={`${BASE_URL}/uploads/penyelia/${row.file_ktp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary me-1"
                            title="Download KTP"
                          >
                            <i className="fas fa-id-card me-1"></i>KTP
                          </a>
                        ) : (
                          <span className="text-muted small me-1">-</span>
                        )}
                        {row.file_foto ? (
                          <a
                            href={`${BASE_URL}/uploads/penyelia/${row.file_foto}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary me-1"
                            title="Download Foto Diri"
                          >
                            <i className="fas fa-image me-1"></i>Foto
                          </a>
                        ) : (
                          <span className="text-muted small me-1">-</span>
                        )}
                        {row.bukti_pembayaran ? (
                          <a
                            href={`${BASE_URL}/uploads/penyelia/${row.bukti_pembayaran}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-warning"
                            title="Download Bukti Bayar"
                          >
                            <i className="fas fa-receipt me-1"></i>Bukti Bayar
                          </a>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="success"
                          className="me-1"
                          onClick={() => openTerimaModal(row)}
                        >
                          <i className="fas fa-check me-1"></i>Terima
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          className="me-1"
                          onClick={() => openTolakModal(row)}
                        >
                          <i className="fas fa-times me-1"></i>Tolak
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => openHapusModal(row)}
                          title="Hapus data calon"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Konfirmasi Terima */}
      <Modal show={showTerimaModal} onHide={() => !actionLoading && setShowTerimaModal(false)} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title><i className="fas fa-check-circle me-2"></i>Konfirmasi Penerimaan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p>Apakah Anda yakin ingin <strong>menerima</strong> pendaftaran berikut?</p>
          {targetTerima && (
            <table className="table table-sm table-borderless">
              <tbody>
                <tr><td className="text-muted">Nama</td><td className="fw-semibold">{targetTerima.nama}</td></tr>
                <tr><td className="text-muted">Email</td><td>{targetTerima.email}</td></tr>
                <tr><td className="text-muted">Jenis Pelatihan</td><td>{jenisPelatihanLabel[targetTerima.jenis_pelatihan] || targetTerima.jenis_pelatihan}</td></tr>
              </tbody>
            </table>
          )}
          <p className="text-muted small mb-0">Status akan berubah menjadi <Badge bg="primary">PESERTA PELATIHAN</Badge></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTerimaModal(false)} disabled={actionLoading}>
            Batal
          </Button>
          <Button variant="success" onClick={handleTerima} disabled={actionLoading}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-check me-1"></i>}
            Ya, Terima
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Tolak */}
      <Modal show={showTolakModal} onHide={() => !actionLoading && setShowTolakModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><i className="fas fa-times-circle me-2"></i>Tolak Pendaftaran</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          {targetTolak && (
            <p>
              Tolak pendaftaran <strong>{targetTolak.nama}</strong> ({targetTolak.email})?
            </p>
          )}
          <Form.Group>
            <Form.Label className="fw-semibold">
              Alasan Penolakan <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={250}
              placeholder="Tuliskan alasan penolakan..."
              value={alasanTolak}
              onChange={(e) => { setAlasanTolak(e.target.value); setAlasanError(""); }}
              isInvalid={!!alasanError}
            />
            <Form.Control.Feedback type="invalid">{alasanError}</Form.Control.Feedback>
            <div className="text-end text-muted small mt-1">{alasanTolak.length}/250</div>
          </Form.Group>
          <p className="text-muted small mb-0 mt-2">Status akan berubah menjadi <Badge bg="danger">DITOLAK</Badge></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTolakModal(false)} disabled={actionLoading}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleTolak} disabled={actionLoading}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-times me-1"></i>}
            Tolak Pendaftaran
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal Hapus */}
      <Modal show={showHapusModal} onHide={() => !actionLoading && setShowHapusModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><i className="fas fa-trash me-2"></i>Hapus Data Calon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p>Apakah Anda yakin ingin <strong>menghapus permanen</strong> data calon berikut?</p>
          {targetHapus && (
            <table className="table table-sm table-borderless">
              <tbody>
                <tr><td className="text-muted">No. Register</td><td className="fw-semibold">{targetHapus.no_register}</td></tr>
                <tr><td className="text-muted">Nama</td><td className="fw-semibold">{targetHapus.nama}</td></tr>
                <tr><td className="text-muted">Email</td><td>{targetHapus.email}</td></tr>
                <tr><td className="text-muted">Jenis Pelatihan</td><td>{jenisPelatihanLabel[targetHapus.jenis_pelatihan] || targetHapus.jenis_pelatihan}</td></tr>
              </tbody>
            </table>
          )}
          <Alert variant="warning" className="mb-0 py-2">
            <i className="fas fa-exclamation-triangle me-1"></i>
            Tindakan ini tidak dapat dibatalkan. Data dan akun peserta akan dihapus permanen.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHapusModal(false)} disabled={actionLoading}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleHapus} disabled={actionLoading}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-trash me-1"></i>}
            Ya, Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PesertaDaftarCalon;